import * as React from 'react';
import * as _ from 'underscore';

import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  ButtonType,
  ButtonWidget,
  DialogPositioningBehavior,
  DialogType,
  TabStyles
} from '@tableau/widgets';

import { Messages } from '../../codegen/Localize';
import { cssClass, divWithStyle } from '../../style';
import { ReactModalOptions } from '../../ts/services/ReactModalService';
import { CharacterCounter } from './CharacterCounter';
import { buttonStyleSubmitting, cancelButtonStyle, overLimitCounter, textAreaCssClass } from './styles/ExternalContentActionsStyles';

const DESCRIPTION_DIALOG_TEST_ID: string = 'description-dialog';
const DESCRIPTION_DIALOG_CANCEL_TEST_ID: string = DESCRIPTION_DIALOG_TEST_ID + '-cancel';
const DESCRIPTION_DIALOG_SAVE_TEST_ID: string = DESCRIPTION_DIALOG_TEST_ID + '-save';

const MESSAGE_HEIGHT: number = 190;
const DIALOG_WIDTH: number = 500;
const DESCRIPTION_MAXIMUM_LENGTH = 4000;

const HelpTextDiv = divWithStyle(DESCRIPTION_DIALOG_TEST_ID, '-help-text', {
  paddingTop: TabStyles.Spacing.SmallSpace,
  paddingBottom: TabStyles.Spacing.SmallSpace,
});

const textAreaClass = textAreaCssClass(DESCRIPTION_DIALOG_TEST_ID, MESSAGE_HEIGHT);

const CancelAndSendButtonClass = cssClass('CancelAndSendButtonClass', '', {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end'
});

export interface DescriptionDialogProps {
  readonly initial: DescriptionDialogState;
  readonly submit: (args: DescriptionDialogState) => void;
  readonly cancel: () => void;
}

export interface DescriptionDialogState {
  readonly description: string;
}

export interface DescriptionDialogStateAndLifecycle extends DescriptionDialogState {
  readonly isSubmitting: boolean;
  readonly isDirty: boolean;
  readonly isValid: boolean;
}

function checkIsValid(current: DescriptionDialogState): boolean {
  return current.description.length <= DESCRIPTION_MAXIMUM_LENGTH;
}
function checkIsDirty(current: DescriptionDialogState, initial: DescriptionDialogState): boolean {
  return !_.isEqual(initial, current);
}

function getFormState(state: DescriptionDialogState | DescriptionDialogStateAndLifecycle): DescriptionDialogState {
  return {
    description: state.description
  };
}

export class DescriptionDialog extends React.Component<DescriptionDialogProps, DescriptionDialogStateAndLifecycle> {
  public constructor(props: DescriptionDialogProps) {
    super(props);
    this.state = {
      description: props.initial.description || '',
      isValid: true,
      isDirty: false,
      isSubmitting: false,
    };
  }

  private onDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const description = event.target.value;

    this.setState((state, props) => {
      const form = getFormState(state);
      const updated: DescriptionDialogState = { ...form, description };

      return { ...updated, isDirty: checkIsDirty(updated, props.initial), isValid: checkIsValid(updated) };
    });
  }

  private onSubmit = () => this.setState({ isSubmitting: true }, () => this.props.submit(getFormState(this.state)));

  private onCancel = () => this.setState({ isSubmitting: true }, this.props.cancel);

  public render(): React.ReactNode {
    return (
      <React.Fragment>
        <HelpTextDiv>{Messages.about()}</HelpTextDiv>
        <textarea onChange={this.onDescriptionChange}
          className={textAreaClass}
          value={this.state.description} />
        <CharacterCounter
          value={this.state.description}
          maximum={DESCRIPTION_MAXIMUM_LENGTH}
          style={this.state.isValid ? null : overLimitCounter} />
        <div className={CancelAndSendButtonClass}>
          <ButtonWidget testId={DESCRIPTION_DIALOG_CANCEL_TEST_ID}
            buttonType={ButtonType.Outline}
            disabled={this.state.isSubmitting}
            handleClick={this.onCancel}
            style={cancelButtonStyle}>
            {Messages.cancel()}
          </ButtonWidget>
          <ButtonWidget testId={DESCRIPTION_DIALOG_SAVE_TEST_ID}
            buttonType={ButtonType.Go}
            disabled={this.state.isSubmitting || !this.state.isDirty || !this.state.isValid}
            style={ this.state.isSubmitting ? buttonStyleSubmitting : undefined }
            handleClick={this.onSubmit}>
            {
              this.state.isSubmitting ?
                <div style={{ marginTop: TabStyles.Spacing.SmallSpace! }}>
                  <ActivitySpinnerWidget spinnerSize={ActivitySpinnerSize.Small} />
                </div> :
                <span>{Messages.save()}</span>
            }
          </ButtonWidget>
        </div>
      </React.Fragment>
    );
  }
}

export function descriptionDialogProps(props: DescriptionDialogProps): ReactModalOptions {
  return {
    title: Messages.editDescription2(),
    testId: DESCRIPTION_DIALOG_TEST_ID,
    type: DialogType.NormalWithTitleBar,
    positioningBehavior: DialogPositioningBehavior.Centered,
    dialogWidth: DIALOG_WIDTH,
    dialogContent: <DescriptionDialog {...props} />
  };
}
